"use client"

import { useRef, useMemo } from "react"
import { useFrame } from "@react-three/fiber"
import { useTexture } from "@react-three/drei"
import * as THREE from "three"

interface ParticleSphereProps {
    images?: string[]
}

const DEFAULT_IMAGES = [
    "https://plus.unsplash.com/premium_photo-1750075345490-1d9d908215c3?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwxfHx8ZW58MHx8fHx8",
    "https://images.unsplash.com/photo-1758445048994-d337f97acf4c?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwyfHx8ZW58MHx8fHx8",
    "https://images.unsplash.com/photo-1758637612244-08a79efb7cb7?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwzfHx8ZW58MHx8fHx8",
    "https://images.unsplash.com/photo-1758621518225-9248e65dbaee?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHw2fHx8ZW58MHx8fHx8",
    "https://images.unsplash.com/photo-1758640920711-8ad5dbcb5214?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwxMXx8fGVufDB8fHx8fA%3D%3D",
    "https://plus.unsplash.com/premium_photo-1728419694854-7848ad37e9e5?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwxM3x8fGVufDB8fHx8fA%3D%3D",
]

export function ParticleSphere({ images = DEFAULT_IMAGES }: ParticleSphereProps) {
    const PARTICLE_COUNT = 1500
    const PARTICLE_SIZE_MIN = 0.005
    const PARTICLE_SIZE_MAX = 0.010
    const SPHERE_RADIUS = 9
    const POSITION_RANDOMNESS = 4
    const ROTATION_SPEED_X = 0.0
    const ROTATION_SPEED_Y = 0.0005
    const PARTICLE_OPACITY = 1

    // Use the length of the passed images array if available, otherwise default logic
    const IMAGE_COUNT = images.length
    const IMAGE_SIZE = 1.5

    const groupRef = useRef<THREE.Group>(null)

    // Load textures from the passed images prop
    const textures = useTexture(images)

    useMemo(() => {
        // Determine if textures is an array or single texture (drei behavior)
        const textureArray = Array.isArray(textures) ? textures : [textures]

        textureArray.forEach((texture) => {
            if (texture) {
                texture.wrapS = THREE.ClampToEdgeWrapping
                texture.wrapT = THREE.ClampToEdgeWrapping
                texture.flipY = false
                // texture.colorSpace = THREE.SRGBColorSpace // Modern three.js handling
            }
        })
    }, [textures])

    const particles = useMemo(() => {
        const particles = []

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const phi = Math.acos(-1 + (2 * i) / PARTICLE_COUNT)
            const theta = Math.sqrt(PARTICLE_COUNT * Math.PI) * phi

            const radiusVariation = SPHERE_RADIUS + (Math.random() - 0.5) * POSITION_RANDOMNESS

            const x = radiusVariation * Math.cos(theta) * Math.sin(phi)
            const y = radiusVariation * Math.cos(phi)
            const z = radiusVariation * Math.sin(theta) * Math.sin(phi)

            particles.push({
                position: [x, y, z] as [number, number, number],
                scale: Math.random() * (PARTICLE_SIZE_MAX - PARTICLE_SIZE_MIN) + PARTICLE_SIZE_MIN,
                color: new THREE.Color().setHSL(
                    Math.random() * 0.1 + 0.05,
                    0.8,
                    0.6 + Math.random() * 0.3,
                ),
                rotationSpeed: (Math.random() - 0.5) * 0.01,
            })
        }

        return particles
    }, [PARTICLE_COUNT, SPHERE_RADIUS, POSITION_RANDOMNESS, PARTICLE_SIZE_MIN, PARTICLE_SIZE_MAX])

    const orbitingImages = useMemo(() => {
        const meshImages = []
        const textureArray = Array.isArray(textures) ? textures : [textures]

        // If we have fewer images than needed positions, we loop them or just show what we have.
        // The original code had a fixed IMAGE_COUNT. Let's use the actual number of images passed.
        const count = textureArray.length
        if (count === 0) return []

        for (let i = 0; i < count; i++) {
            // Distribute evenly
            const angle = (i / count) * Math.PI * 2
            const x = SPHERE_RADIUS * Math.cos(angle)
            const y = 0
            const z = SPHERE_RADIUS * Math.sin(angle)

            const position = new THREE.Vector3(x, y, z)
            const center = new THREE.Vector3(0, 0, 0)
            const outwardDirection = position.clone().sub(center).normalize()

            const euler = new THREE.Euler()
            const matrix = new THREE.Matrix4()
            matrix.lookAt(position, position.clone().add(outwardDirection), new THREE.Vector3(0, 1, 0))
            euler.setFromRotationMatrix(matrix)

            euler.z += Math.PI

            meshImages.push({
                position: [x, y, z] as [number, number, number],
                rotation: [euler.x, euler.y, euler.z] as [number, number, number],
                textureIndex: i,
                color: new THREE.Color().setHSL(Math.random(), 0.7, 0.6),
            })
        }

        return meshImages
    }, [SPHERE_RADIUS, textures])

    useFrame((state) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += ROTATION_SPEED_Y
            groupRef.current.rotation.x += ROTATION_SPEED_X
        }
    })

    // Ensure textures is treated as an array for access
    const textureArray = Array.isArray(textures) ? textures : [textures]

    return (
        <group ref={groupRef}>
            {particles.map((particle, index) => (
                <mesh key={`p-${index}`} position={particle.position} scale={particle.scale}>
                    <sphereGeometry args={[1, 8, 6]} />
                    <meshBasicMaterial color={particle.color} transparent opacity={PARTICLE_OPACITY} />
                </mesh>
            ))}

            {orbitingImages.map((image, index) => (
                <mesh key={`image-${index}`} position={image.position} rotation={image.rotation}>
                    <planeGeometry args={[IMAGE_SIZE, IMAGE_SIZE]} />
                    <meshBasicMaterial map={textureArray[image.textureIndex]} opacity={1} side={THREE.DoubleSide} />
                </mesh>
            ))}
        </group>
    )
}
